'use client'

import { useMemo } from 'react'

import { toExchangeEnum, tradeVenueToExchangeKey } from '@/lib/proto/util'
import { useUserAccounts } from '@/service/account.hook'
import { Account } from '@/types/generated/common/account'
import { Exchange } from '@/types/generated/common/types'

export interface AccountLookup {
  accountId: string
  name: string
  exchange: Exchange
}

/** accountId -> {name, exchange}, built once from the user's own account list. */
export function useAccountLookupMap(): Map<string, AccountLookup> {
  const { data } = useUserAccounts()
  return useMemo(() => {
    const map = new Map<string, AccountLookup>()
    ;(data?.accounts ?? []).forEach((account: Account) => {
      map.set(account.accountId, {
        accountId: account.accountId,
        name: account.name,
        exchange: toExchangeEnum(tradeVenueToExchangeKey(account.tradeVenue)),
      })
    })
    return map
  }, [data])
}

/** accountId -> Exchange only — for call sites (e.g. Trigger row mapping) that don't
 * need the account name, built off the same lookup map. */
export function useAccountIdToExchangeMap(): Map<string, Exchange> {
  const lookup = useAccountLookupMap()
  return useMemo(() => {
    const map = new Map<string, Exchange>()
    lookup.forEach((entry, accountId) => map.set(accountId, entry.exchange))
    return map
  }, [lookup])
}
